import type { MutableRefObject } from 'react';
import type { MapRef } from 'react-map-gl';
import { getItem } from '../../io';

const FALLBACK_LAYER = 'base';

type Viewport = {
  longitude: number;
  latitude: number;
  zoom: number;
};

type IncomingMarkerPoint = {
  id: string;
  label: string;
  coordinate: [number, number];
  layer?: string | null;
  ordinal?: number | null;
};

export type MarkerPointInput = {
  id: string;
  label: string;
  coordinate: [number, number];
  layer: string | null;
  ordinal: number | null;
};

export type MapSettingsInput = {
  layer: string;
  longitude: number;
  latitude: number;
  zoom: number;
};

export type ShareVariables = {
  markerPoints: MarkerPointInput[];
  mapSettings: MapSettingsInput;
};

type BuildShareVariablesArgs = {
  mapRef: MutableRefObject<MapRef>;
  layer: string | null;
  markerPoints: IncomingMarkerPoint[];
  sharedInitialCoordinates: string | null;
};

const isFiniteNumber = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n);

const parseCoordsString = (coords: string | null): Viewport | null => {
  if (!coords) return null;
  const [lon, lat, zoom] = coords.split(',').map(parseFloat);
  if (!isFiniteNumber(lon) || !isFiniteNumber(lat) || !isFiniteNumber(zoom)) {
    return null;
  }
  return { longitude: lon, latitude: lat, zoom };
};

const getMapViewport = (
  mapRef: MutableRefObject<MapRef>
): Viewport | null => {
  const map = mapRef?.current?.getMap();
  if (!map) return null;
  try {
    const center = map.getCenter();
    const zoom = map.getZoom();
    if (
      !isFiniteNumber(center?.lng) ||
      !isFiniteNumber(center?.lat) ||
      !isFiniteNumber(zoom)
    ) {
      return null;
    }
    return { longitude: center.lng, latitude: center.lat, zoom };
  } catch {
    return null;
  }
};

// Strips Apollo's __typename and any unknown fields; the shared-map query
// hydrates markers with __typename which the MarkerPointInput schema rejects.
const sanitizeMarkerPoint = (m: IncomingMarkerPoint): MarkerPointInput => ({
  id: m.id,
  label: m.label,
  coordinate: m.coordinate,
  layer: m.layer ?? null,
  ordinal: typeof m.ordinal === 'number' ? m.ordinal : null,
});

const buildShareVariables = ({
  mapRef,
  layer,
  markerPoints,
  sharedInitialCoordinates,
}: BuildShareVariablesArgs): ShareVariables | null => {
  const viewport =
    getMapViewport(mapRef) ??
    parseCoordsString(sharedInitialCoordinates) ??
    parseCoordsString(getItem('initialCoordinates'));

  if (!viewport) return null;

  const resolvedLayer = layer && layer.length > 0 ? layer : FALLBACK_LAYER;

  return {
    markerPoints: (markerPoints ?? []).map(sanitizeMarkerPoint),
    mapSettings: {
      layer: resolvedLayer,
      longitude: viewport.longitude,
      latitude: viewport.latitude,
      zoom: viewport.zoom,
    },
  };
};

export { buildShareVariables };
